import React, { useCallback, useMemo, useState } from 'react';
import { Grid2 } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Collapse from '@mui/material/Collapse';
import { ListItem } from '../../ListContext';
import { ShowData, ShowContextProvider } from 'framework/show';
import { getVisibility } from 'framework/utils';

export interface RowGridProps {
  data: ListItem;
  template: React.ReactElement[];
  buttons?: React.ReactElement[];
  rowProps?: ((data: ShowData) => any) | Record<string, any>;
  rowKeySource?: string;
  isExpandableItem?: (data: ShowData) => boolean;
  buttonContainerSize?: number;
  expanded?: boolean;
  expandable?: boolean;
  expandableSection?: React.ReactNode;
  containerSx?: Record<string, any>;
  expandIconInFirstCell?: boolean;
  useCollapseForExpandable?: boolean;
  renderExpandableSectionInsideBox?: boolean;
}

const EXPAND_ANIMATION_DURATION = 400;
const COLLAPSED_HEIGHT = 65;
const EXPANDED_HEIGHT = 500;

const RowGrid = ({
  data,
  template,
  buttons = [],
  rowProps = {},
  rowKeySource = 'id',
  expanded: initialExpanded = false,
  expandable = true,
  isExpandableItem,
  buttonContainerSize,
  expandableSection,
  containerSx = {},
  expandIconInFirstCell = false,
  useCollapseForExpandable = true,
  renderExpandableSectionInsideBox = false,
}: RowGridProps) => {
  const dataInstance = useMemo(() => new ShowData(data), [data]);

  const { sxRowContainer = {}, ...props } = typeof rowProps === 'function' ? rowProps(dataInstance) : rowProps;

  const [expanded, setExpanded] = useState(initialExpanded);
  const [delayedExpanded, setDelayedExpanded] = useState(initialExpanded);

  const expandableSectionContent = () => {
    if (!expandableSection) return null;

    if (useCollapseForExpandable) {
      return (
        <Collapse in={expanded} unmountOnExit timeout={400}>
          {expandableSection}
        </Collapse>
      );
    }

    return expanded ? expandableSection : null;
  };

  const shouldRenderExpandButton = useMemo(() => {
    return expandable && (isExpandableItem ? isExpandableItem(dataInstance) : true);
  }, [expandable, isExpandableItem, dataInstance]);

  const toggleExpanded = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (!shouldRenderExpandButton) return;

      const next = !expanded;
      setExpanded(next);

      if (!expanded) {
        setDelayedExpanded(true);
      } else {
        setTimeout(() => setDelayedExpanded(false), EXPAND_ANIMATION_DURATION);
      }
    },
    [expanded, shouldRenderExpandButton],
  );

  const rowContainerSx = {
    borderRadius: 1,
    px: 2,
    py: 1,
    my: 1,
    border: '1px solid',
    borderColor: 'border.default',
    cursor: expandable && shouldRenderExpandButton ? 'pointer' : 'default',
    overflow: 'hidden',
    maxHeight: !expandable ? '100%' : expanded ? EXPANDED_HEIGHT : COLLAPSED_HEIGHT,
    transition: `max-height ${EXPAND_ANIMATION_DURATION}ms ease`,
    width: '100%',
    boxSizing: 'border-box',
    position: 'relative',
    ...sxRowContainer,
    ...containerSx,
  };

  return (
    <ShowContextProvider value={{ data: dataInstance }}>
      <Box onClick={toggleExpanded} sx={rowContainerSx}>
        <Grid2 container wrap="nowrap" justifyContent="space-between" alignItems="flex-start" {...props}>
          {!expandIconInFirstCell && shouldRenderExpandButton ? (
            <Grid2 size={0.5} minWidth="23px">
              <IconButton
                size="small"
                onClick={toggleExpanded}
                sx={{
                  p: 0,
                  width: '23px',
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                }}
              >
                <KeyboardArrowDownIcon sx={{ width: '21px' }} />
              </IconButton>
            </Grid2>
          ) : (
            !expandIconInFirstCell && expandable && <Grid2 size={0.5} minWidth="23px" />
          )}

          {template.map((element, i) => {
            const { size = 2 } = element.props;
            const isFirst = i === 0;

            return (
              <Grid2
                size={size}
                key={`cell-${data[rowKeySource]}-${element.key || `template-${i}`}`}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                {isFirst && expandIconInFirstCell && (
                  <IconButton
                    size="small"
                    onClick={toggleExpanded}
                    sx={{
                      p: 0,
                      width: '23px',
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                      visibility: shouldRenderExpandButton ? 'visible' : 'hidden',
                    }}
                  >
                    <KeyboardArrowDownIcon sx={{ width: '21px' }} />
                  </IconButton>
                )}
                {React.cloneElement(element, {
                  ...element.props,
                  expanded: delayedExpanded,
                })}
              </Grid2>
            );
          })}

          {buttons.length > 0 && (
            <Grid2
              size={buttonContainerSize}
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              onClick={e => e.stopPropagation()}
            >
              {buttons.map((btn, i) => {
                const { isVisible: initialIsVisible } = btn.props;
                const isVisible = getVisibility(data, initialIsVisible);
                return isVisible ? (
                  // eslint-disable-next-line react/no-array-index-key
                  <Box key={`btn-${i}`} mx={0.5}>
                    {btn}
                  </Box>
                ) : null;
              })}
            </Grid2>
          )}
        </Grid2>
        {renderExpandableSectionInsideBox && expandableSectionContent()}
      </Box>
      {!renderExpandableSectionInsideBox && expandableSectionContent()}
    </ShowContextProvider>
  );
};

export default RowGrid;
